import { z } from "zod";
// Shared, generic self-profile validation for the MLX web app, its profile API
// route, and the admin-control self-profile broker. No tenant/customer
// specifics: a user may edit their own first/last name and avatar only.
export const PROFILE_NAME_MAX_LENGTH = 100;
export const PROFILE_AVATAR_ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
];
/** `accept` attribute value for an avatar file input. */
export const PROFILE_AVATAR_ACCEPT = PROFILE_AVATAR_ALLOWED_MIME_TYPES.join(",");
/** Hard cap on avatar upload size (5 MiB). */
export const PROFILE_AVATAR_MAX_BYTES = 5 * 1024 * 1024;
/** Read an avatar request body without buffering beyond the upload cap. */
export async function readProfileAvatarRequestBody(request) {
    const declared = Number(request.headers.get("content-length") ?? "");
    if (Number.isFinite(declared) && declared > PROFILE_AVATAR_MAX_BYTES) {
        return { ok: false, message: "Avatar exceeds the maximum size." };
    }
    if (!request.body) {
        return {
            ok: true,
            bytes: new Uint8Array(),
            contentType: request.headers.get("content-type"),
        };
    }
    const reader = request.body.getReader();
    const chunks = [];
    let total = 0;
    while (true) {
        const { done, value } = await reader.read();
        if (done)
            break;
        total += value.byteLength;
        if (total > PROFILE_AVATAR_MAX_BYTES) {
            await reader.cancel();
            return { ok: false, message: "Avatar exceeds the maximum size." };
        }
        chunks.push(value);
    }
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.byteLength;
    }
    return {
        ok: true,
        bytes,
        contentType: request.headers.get("content-type"),
    };
}
const profileNameField = z
    .string()
    .trim()
    .max(PROFILE_NAME_MAX_LENGTH, `Name must be ${PROFILE_NAME_MAX_LENGTH} characters or fewer.`);
// firstName/lastName are each optional; an explicit empty string clears that
// field. At least one field must be present so a no-op update is rejected.
export const profileNameUpdateSchema = z
    .object({
    firstName: profileNameField.optional(),
    lastName: profileNameField.optional(),
})
    .refine((value) => value.firstName !== undefined || value.lastName !== undefined, { message: "Provide a first or last name to update." });
export function parseProfileNameUpdate(input) {
    const parsed = profileNameUpdateSchema.safeParse(input);
    if (parsed.success)
        return { ok: true, value: parsed.data };
    const message = parsed.error.issues[0]?.message ?? "Invalid profile name update.";
    return { ok: false, message };
}
export function normalizeAvatarMimeType(contentType) {
    if (!contentType)
        return null;
    const value = contentType.split(";")[0]?.trim().toLowerCase();
    return (PROFILE_AVATAR_ALLOWED_MIME_TYPES.find((type) => type === value) ?? null);
}
export function validateAvatarUpload(input) {
    const mimeType = normalizeAvatarMimeType(input.contentType);
    if (!mimeType) {
        return {
            ok: false,
            code: "unsupported_type",
            message: "Avatar must be a JPEG, PNG, or WebP image.",
        };
    }
    if (!Number.isFinite(input.byteLength) || input.byteLength <= 0) {
        return { ok: false, code: "empty", message: "Avatar file is empty." };
    }
    if (input.byteLength > PROFILE_AVATAR_MAX_BYTES) {
        return {
            ok: false,
            code: "too_large",
            message: `Avatar must be ${Math.floor(PROFILE_AVATAR_MAX_BYTES / (1024 * 1024))} MB or smaller.`,
        };
    }
    if (input.bytes && !avatarBytesMatchMimeType(input.bytes, mimeType)) {
        return {
            ok: false,
            code: "invalid_content",
            message: "Avatar contents do not match the selected image type.",
        };
    }
    return { ok: true, mimeType };
}
function avatarBytesMatchMimeType(bytes, mimeType) {
    if (mimeType === "image/jpeg") {
        return (bytes.length >= 3 &&
            bytes[0] === 0xff &&
            bytes[1] === 0xd8 &&
            bytes[2] === 0xff);
    }
    if (mimeType === "image/png") {
        const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
        return signature.every((value, index) => bytes[index] === value);
    }
    return (bytes.length >= 12 &&
        String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" &&
        String.fromCharCode(...bytes.slice(8, 12)) === "WEBP");
}
export function profileAvatarExtensionForMime(mimeType) {
    switch (mimeType) {
        case "image/jpeg":
            return "jpg";
        case "image/png":
            return "png";
        case "image/webp":
            return "webp";
    }
}
export function profileDisplayName(dto) {
    const name = [dto.firstName, dto.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ")
        .trim();
    return name || null;
}
